import { KnowledgeBasesV2Service } from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SpotToast } from "../SpotToast";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { DeleteDialog } from "./DeleteDialog";
import { EntityDialogTitle } from "./EntityDialogTitle";
import { useCloseTabAndRedirect } from "@/components/Layouts/ToolLayout/currentTool";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";

interface IFlowDeleteDialog {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  kbId: string;
  kbName: string;
  kbIcon?: string;
}

export const KBDeleteDialog = ({
  isOpen,
  onOpenChange,
  kbId,
  kbName,
  kbIcon,
}: IFlowDeleteDialog) => {
  const { spotGroup } = useSpotUser();
  const closeTabAndRedirect = useCloseTabAndRedirect();
  const queryClient = useQueryClient();

  const deleteKnowledgeBaseMutation = useMutation({
    mutationFn: KnowledgeBasesV2Service.deleteKnowledgeBaseV2,
    onSuccess: () => {
      SpotToast.success("Knowledge base deleted");
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.KNOWLEDGE_BASES_V2],
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.WORKSHOP_RECENT],
      });
      closeTabAndRedirect(kbId);
    },
    onError: () => {
      SpotToast.error("Error deleting the knowledge base.");
    },
    onSettled: () => {
      onOpenChange(false);
    },
  });

  return (
    <DeleteDialog
      title={
        <EntityDialogTitle
          action="Delete"
          icon={kbIcon || "kb"}
          name={kbName}
        />
      }
      text={`Are you sure you want to delete this knowledge base? Knowledge bases are not recoverable.`}
      onDelete={() => {
        analytics_track(ANALYTICS_EVENTS.KNOWLEDGE_BASE_DELETE);

        deleteKnowledgeBaseMutation.mutate({
          path: {
            group_id: spotGroup.id,
            knowledge_base_id: kbId,
          },
        });
      }}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isLoading={deleteKnowledgeBaseMutation.status === "pending"}
    />
  );
};
